import React, { useEffect } from "react";

import { useProfileData } from "@/services/UserService";
import SplashScreen from "@/features/pages/app/SplashScreen";
import Icon from "@/components/misc/Icon";
import { IS_STAGING_ENV } from "@/config/app";

import SocialLinksDisplay from "./components/SocialLinksDisplay";
import {
  StyledCard,
  StyledContainer,
  StyledCtaButton,
  StyledDetailsCardContainer,
  StyledFooter,
  StyledHeading,
  StyledId,
  StyledPage,
  StyledProfileContentContainer,
  StyledRightContainer,
  StyledSection,
  StyledSectionContent,
  StyledSectionHeading,
  StyledUserImage,
} from "./PaypipeIdPage.styles";
import { getDummyPaypipeId, getUserDisplayName } from "./PaypipeIdPage.utils";

import SectionTitle from "./components/SectionTitle";
import Section from "./components/Section";
import SkillsDisplay from "./components/SkillsDisplay";
import PortfolioDisplay from "./components/PortfolioDisplay";
import PackageDisplay from "./components/PackageDisplay";
import Feature from "./components/Feature";

const PaypipeIdPage: React.FC<{}> = () => {
  const { profileData, isProfileDataLoading, accountData } = useProfileData();

  useEffect(() => {
    if (IS_STAGING_ENV) {
      console.log("DEBUG", {
        accountData,
        profileData,
      });
    }
  }, [accountData, profileData]);

  if (isProfileDataLoading || !accountData) {
    return <SplashScreen />;
  }

  const { given_name, family_name, picture } = accountData;
  const { about_me, skills, portfolios, packages } = profileData;

  return (
    <StyledPage>
      <StyledDetailsCardContainer>
        <StyledCard>
          <StyledHeading size="lg">
            {getUserDisplayName(given_name, family_name)}
          </StyledHeading>

          <StyledUserImage
            className="mt-5"
            src={picture}
            alt=""
            width={32}
            height={32}
          />

          <StyledId>{getDummyPaypipeId(accountData)}</StyledId>

          <small className="mt-4">
            @ 2022 {accountData?.given_name}. All Rights Reserved
          </small>

          <SocialLinksDisplay
            className="mt-4"
            socialLinks={profileData.links}
          />

          <StyledCtaButton className="mt-4 w-100">
            <Icon
              isSrcRelative
              src="rocket.svg"
              size="xs"
              colorVariant="white"
            />
            Get in touch
          </StyledCtaButton>
        </StyledCard>
      </StyledDetailsCardContainer>

      <StyledContainer>
        <StyledRightContainer>
          <StyledProfileContentContainer>
            <StyledSection $fullHeight={false}>
              <SectionTitle title="About me" icon="home.svg" />
              <StyledSectionContent size="lg" className="mt-5">
                {about_me}
              </StyledSectionContent>
            </StyledSection>

            {false && (
              <Section
                title="Services"
                icon="box.svg"
                heading={
                  <>
                    My <b>Specializations</b>
                  </>
                }
              />
            )}

            <Section title="Skills" icon="box.svg" heading="Skills and tools">
              <SkillsDisplay skills={skills} />
            </Section>

            {!!portfolios.length && (
              <Section
                title="Portfolio"
                icon="box.svg"
                heading={
                  <>
                    Featured <b>Projects</b>
                  </>
                }
              >
                <PortfolioDisplay portfolios={portfolios} />
              </Section>
            )}

            {false && (
              <Section
                title="Testimonials"
                icon="box.svg"
                heading="What clients had to say"
              />
            )}

            {!!packages.length && (
              <Section
                title="Packages"
                icon="box.svg"
                heading={
                  <>
                    My <b>Priced Packages</b>
                  </>
                }
              >
                <PackageDisplay packages={packages} />
              </Section>
            )}

            <StyledSection $fullHeight={false}>
              <StyledSectionHeading size="lg" className="mt-5">
                Let's Work <b>Together!</b>
              </StyledSectionHeading>
              <StyledCtaButton className="mt-5">
                <Icon
                  isSrcRelative
                  src="rocket.svg"
                  size="xs"
                  colorVariant="white"
                />
                Get in touch
              </StyledCtaButton>
            </StyledSection>
          </StyledProfileContentContainer>
        </StyledRightContainer>
      </StyledContainer>

      <StyledFooter>
        <StyledContainer>
          <StyledRightContainer>
            <div className="heading">This could be your profile</div>
            <div className="mt-4 sub-heading">
              Create your own Paypipe profile and streamline your process today.
            </div>

            <SectionTitle
              title="Benefits"
              icon="home.svg"
              className="mt-5"
              variant="light"
            />

            <div className="mt-5 features-container">
              {["Benefit 1", "Benefit 2", "Benefit 3"].map((item, i) => (
                <Feature key={i} title={item} variant="light" />
              ))}
            </div>

            <div className="mt-5 sub-heading">
              Create your own Paypipe profile and streamline your process today.
            </div>

            <div className="mt-5 cta-section">
              <StyledCtaButton colorVariant="white-primary">
                Create account
              </StyledCtaButton>
              <StyledCtaButton variant="secondary" colorVariant="white-primary">
                Learn More
              </StyledCtaButton>
            </div>
          </StyledRightContainer>
        </StyledContainer>
      </StyledFooter>
    </StyledPage>
  );
};

export default PaypipeIdPage;
